import type { ConnectingDelayJudgementMode, ConnectingDirectionEntry } from '../../../api/types';
import type { RouteMaster } from '../../../data/routes';
import { sortStopsBySequence, type RouteStop } from '../../../data/routeStops';
import type { ValidationIssue } from '../../../utils/validationIssues';
import { hasIssueForTestId } from '../../../utils/validationIssues';
import { LocalizedRouteNicknameFields } from '../parts/LocalizedRouteNicknameFields';
import { RouteDirectionOverridesTable } from '../parts/RouteDirectionOverridesTable';
import { StopSelect } from '../parts/StopSelect';

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 方面ぶんのブロック。
 * `ConnectingDirectionsSection` の 1 方面を編集する。
 *
 * ## 画面構成 (役割ごとに枠を分ける)
 *
 *   1. 「放送文で読み上げる方面名」(必須): `direction_name` (+ 言語別)
 *   2. 「遅延を判定する向き」(任意)      : 線区ごとの向き (`route_directions`)
 *   3. 「遅延を判定する区間」            : `start_stop_id` / `end_stop_id` (section_scheduled のみ)
 *
 * 以前は「判定用」「線区ごとの判定用」「案内文面用」を 2 カラムに混在させていて、
 * どれが何のための入力か分かりにくかった。
 *
 * 判定モードによる項目表示切替:
 *   - whole_line       : 「遅延を判定する向き」/ 区間 (start/end_stop_id) 非表示。
 *                        放送文で読み上げる方面名 (direction_name) のみ入力。
 *   - section_scheduled: 判定する向き + 区間 (start/end_stop_id) + direction_name。
 *   - transfer_aware   : 判定する向き + direction_name (区間は非表示)。
 */
export function ConnectingDirectionBlock({
  lineIndex,
  directionIndex,
  direction,
  routeId,
  routeIds = [],
  judgementMode,
  routes,
  routeStops,
  issues = [],
  languages = ['ja'],

  canRemove,
  isFirst,
  isLast,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  lineIndex: number;
  directionIndex: number;
  direction: ConnectingDirectionEntry;
  routeId: string;
  /**
   * 親接続線の「遅延を検索する線区」(`route_id` + `route_ids`)。
   *
   * 「遅延を判定する向き」表の行になる (線区によって同じ向きを表す
   * `direction_id` が異なるため、線区ごとに選ぶ)。
   */
  routeIds?: readonly string[];
  judgementMode: ConnectingDelayJudgementMode;

  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  /**
   * この方面に紐づく入力エラー (親で `directionIndex` 絞り込み済み)。
   * 方面名 / 区間駅 (開始・終了) の入力欄に赤枠 (`.is-invalid`) を付けるために使う。
   */
  issues?: ValidationIssue[];
  /**
   * コンテンツの放送言語 (`content.languages`)。日本語以外が含まれるとき、
   * 方面名の言語別入力欄 (`direction_name_i18n`) を表示する (モンキーテスト B-1)。
   */
  languages?: readonly string[];

  canRemove: boolean;
  isFirst: boolean;
  isLast: boolean;
  onChange: (next: ConnectingDirectionEntry) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}): React.JSX.Element {
  const directionName = direction.direction_name ?? '';
  const startStopId = direction.start_stop_id ?? '';
  const endStopId = direction.end_stop_id ?? '';

  const isWholeLineMode = judgementMode === 'whole_line';
  const isSectionScheduledMode = judgementMode === 'section_scheduled';

  // 区間駅 (start/end_stop_id) の選択肢は route_id の駅一覧から。
  const stops = sortStopsBySequence(routeId ? routeStops[routeId] : []);

  /** 方面を patch で更新 */
  const emit = (patch: Partial<ConnectingDirectionEntry>): void => {
    onChange({ ...direction, ...patch });
  };

  const testIdSuffix = `${lineIndex}-${directionIndex}`;

  return (
    <div
      className="connecting-direction-block"
      data-testid={`connecting-direction-block-${testIdSuffix}`}
    >
      {/* ヘッダ: ラベル + 操作ボタン (↑↓ / − 削除) */}
      <div
        className="connecting-direction-block-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 4,
          flexWrap: 'wrap',
        }}
      >
        <strong>方面 #{directionIndex + 1}</strong>
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
          <button
            type="button"
            aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} を上へ`}
            data-testid={`connecting-direction-move-up-${testIdSuffix}`}
            disabled={isFirst}
            onClick={onMoveUp}
          >
            ↑
          </button>
          <button
            type="button"
            aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} を下へ`}
            data-testid={`connecting-direction-move-down-${testIdSuffix}`}
            disabled={isLast}
            onClick={onMoveDown}
          >
            ↓
          </button>
          {canRemove ? (
            <button
              type="button"
              className="danger"
              aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} を削除`}
              data-testid={`connecting-direction-remove-${testIdSuffix}`}
              onClick={onRemove}
            >
              − 削除
            </button>
          ) : null}
        </div>
      </div>

      {/* 1. 放送文で読み上げる方面名 (必須)。一番大事な入力なので先頭に置く。 */}
      <section
        className="connecting-section"
        data-testid={`connecting-direction-name-section-${testIdSuffix}`}
      >
        <div className="connecting-section__title">放送文で読み上げる方面名（必須）</div>
        <label>
          <span className="required">方面名</span>
          <div className="direction-name-input-row">
            <input
              type="text"
              aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} 案内文面`}
              data-testid={`connecting-direction-name-${testIdSuffix}`}
              // 未入力で保存した場合に赤枠で強調する (該当欄が一目で分かるように)。
              className={
                hasIssueForTestId(issues, `connecting-direction-name-${testIdSuffix}`)
                  ? 'is-invalid'
                  : ''
              }
              required
              value={directionName}
              placeholder="例: 新宿"
              onChange={(e) => emit({ direction_name: e.target.value })}
            />
            <span
              className="direction-name-suffix"
              data-testid={`connecting-direction-name-suffix-${testIdSuffix}`}
              aria-hidden="true"
            >
              方面
            </span>
          </div>
        </label>
        <p className="connecting-section__lead">
          {judgementMode === 'departure_forecast' ? (
            <>
              発車の見込みでは、接続便の行先 (<code>{'{headsign}'}</code>)
              を取得できないときだけ、この方面名が行先として読み上げられます（例:「新宿」→「新宿は10時5分頃」）。
              遅延の判定には使われません。
            </>
          ) : (
            <>
              放送文の <code>{'{direction_name}'}</code>{' '}
              に入ります（例:「新宿」→「新宿方面は5分遅れ」）。 遅延の判定には使われません。
            </>
          )}
        </p>
        {/*
          モンキーテスト B-1: 方面名の言語別名称 (`direction_name_i18n`)。
          従来は日本語欄しか無く、英中韓の放送でも `{direction_name}` に日本語が
          入り外国語ボイスが読み上げていた。放送言語に英中韓が含まれるときだけ表示する。
        */}
        <LocalizedRouteNicknameFields
          label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} 方面名`}
          summary="各言語の方面名"
          hint={
            <>
              放送言語ごとに <code>{'{direction_name}'}</code>{' '}
              へ展開する方面名を設定できます。未入力の言語は日本語の方面名でそのまま案内されます。
            </>
          }
          languages={languages}
          value={direction.direction_name_i18n}
          japaneseNickname={directionName}
          testIdPrefix={`connecting-direction-name-${testIdSuffix}`}
          onChange={(value) => emit({ direction_name_i18n: value })}
        />
      </section>

      {/*
        2. 遅延を判定する向き (whole_line では方面を判定に使わないため非表示)。

        線区によって同じ向きを表す `direction_id` が異なるため、線区ごとに選ぶ (`route_directions`)。
        方面ブロック共通の「基本の向き」は画面から外した。既存データの共通 `direction_id` は
        表の初期値として表示し、編集した時点で `route_directions` へ移す (詳細は表側の JSDoc)。
        例) 大宮で「宮原・上尾方面」を案内するとき、高崎線は下り (0) だが
            上野東京ライン / 湘南新宿ラインは北行 (1) と揃わない。
      */}
      {!isWholeLineMode ? (
        <section
          className="connecting-section"
          data-testid={`connecting-direction-judge-section-${testIdSuffix}`}
        >
          <div className="connecting-section__title">遅延を判定する向き（任意）</div>
          <RouteDirectionOverridesTable
            testIdPrefix={`connecting-direction-route-directions-${testIdSuffix}`}
            ariaLabelPrefix={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} `}
            routeIds={routeIds}
            routes={routes}
            value={direction.route_directions}
            legacyDirectionId={direction.direction_id}
            onChange={(next) =>
              emit({ route_directions: next.routeDirections, direction_id: next.directionId })
            }
          />
        </section>
      ) : null}

      {/* section_scheduled モードでは区間 (開始/終了駅) を方面ごとに指定 */}
      {isSectionScheduledMode ? (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{ flex: 1 }}>
              <span className="required">遅延判定開始駅</span>
              <StopSelect
                ariaLabel={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} 開始駅`}
                testId={`connecting-direction-start-stop-${testIdSuffix}`}
                value={startStopId}
                stops={stops}
                invalid={hasIssueForTestId(
                  issues,
                  `connecting-direction-start-stop-${testIdSuffix}`,
                )}
                disabled={!routeId}
                onChange={(stopId) => emit({ start_stop_id: stopId })}
              />
            </label>
            <label style={{ flex: 1 }}>
              <span className="required">遅延判定終了駅</span>
              <StopSelect
                ariaLabel={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} 終了駅`}
                testId={`connecting-direction-end-stop-${testIdSuffix}`}
                value={endStopId}
                stops={stops}
                invalid={hasIssueForTestId(issues, `connecting-direction-end-stop-${testIdSuffix}`)}
                disabled={!routeId}
                onChange={(stopId) => emit({ end_stop_id: stopId })}
              />
            </label>
          </div>
          <p className="hint" style={{ margin: '4px 0' }}>
            方面ごとに区間を指定します。区間内に定刻在線している便の遅延で判定します。
          </p>
        </>
      ) : null}
    </div>
  );
}
